'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { useContentList } from '@/lib/hooks/useContent';
import { ContentCard } from '../content/ContentCard';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';

/** Fisher-Yates shuffle – returns a new array in random order. */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
interface ExploreHubsSectionProps {
  /** Randomly shuffle the hub items on each mount. Defaults to false. */
  randomize?: boolean;
}
export function ExploreHubsSection({
  randomize = false
}: ExploreHubsSectionProps) {
  const {
    data,
    isLoading
  } = useContentList({
    type: 'hub',
    limit: 6,
    visibility: 'public'
  });
  const title = 'Communities and Hubs';
  const rawHubs = data?.pages?.flatMap(page => page.content) ?? [];

  // Shuffle once per data change when randomize is enabled
  const hubs = useMemo(() => randomize ? shuffleArray(rawHubs) : rawHubs,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [randomize, JSON.stringify(rawHubs.map(h => h.id))]);
  if (isLoading) {
    return <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({
            length: 3
          }).map((_, i) => <div key={i} className="h-[300px] rounded-2xl bg-gray-200 animate-pulse" />)}
          </div>
        </div>
      </section>;
  }
  if (!hubs.length) return null;
  return <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-0">{title}</h2>
        </div>
        <p className="text-lg text-gray-500 mb-8 max-w-2xl">
          Don't do it alone. Connect. Collaborate. Win.
        </p>

        <Carousel opts={{
        align: 'start',
        loop: false
      }} className="w-full">
          <CarouselContent className="-ml-4">
            {hubs.map(hub => <CarouselItem key={hub.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                <ContentCard content={hub} />
              </CarouselItem>)}
          </CarouselContent>
          <CarouselPrevious className="-left-4 lg:left-0" />
          <CarouselNext className="-right-4 lg:right-0" />
        </Carousel>

        {/* CTA Button */}
        <div className="mt-10 text-center">
          <Link href="/hubs" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30">
            Explore Hubs
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>;
}